import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ReactSortable } from "react-sortablejs";
import classNames from "classnames";
import Label from "../../../components/Label";
import Button from "../../../components/Button";
import styles from "./formulaEditor.module.css";
import Token from "./Token";
import AddConditionElementModal from "./AddConditionElementModal";

const SEED_CONDITIONS = {
  "111-111": {
    ifTokens: [
      { id: "c1", type: "PARAMETER", value: "Spalva" },
      { id: "c2", type: "OPERATOR", value: "==" },
      { id: "c3", type: "VALUE", value: "Šviesus korpusas/Šviesus stalčius" },
      { id: "c4", type: "LOGIC", value: "ARBA" },
      { id: "c5", type: "PARAMETER", value: "Spalva" },
      { id: "c6", type: "OPERATOR", value: "==" },
      { id: "c7", type: "VALUE", value: "Tamsus korpusas/Šviesus stalčius" },
    ],
    thenTokens: [
      { id: "t1", type: "CONSTANT", value: "Kaina" },
      { id: "t2", type: "OPERATOR", value: "-" },
      { id: "t3", type: "NUMBER", value: "30" },
    ],
  },
  "222-222": {
    ifTokens: [
      { id: "c1", type: "PARAMETER", value: "Spalva" },
      { id: "c2", type: "OPERATOR", value: "==" },
      { id: "c3", type: "VALUE", value: "Rudas korpusas" },
    ],
    thenTokens: [
      { id: "t1", type: "CONSTANT", value: "Kaina" },
      { id: "t2", type: "OPERATOR", value: "+" },
      { id: "t3", type: "NUMBER", value: "50" },
    ],
  },
};

const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const ConditionBlock = ({ parameters = [], disableChange, onConditionChange }) => {
  const { configurationId } = useParams();
  const seed = SEED_CONDITIONS[configurationId];

  const [ifTokens, setIfTokens] = useState(seed?.ifTokens || []);
  const [thenTokens, setThenTokens] = useState(seed?.thenTokens || []);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    onConditionChange?.({ ifTokens, thenTokens });
    // eslint-disable-next-line
  }, [JSON.stringify(ifTokens), JSON.stringify(thenTokens)]);

  const hasCondition = ifTokens.length > 0 || thenTokens.length > 0;

  const handleAdd = (token, target) => {
    const newToken = { ...token, id: makeId() };
    if (target === "IF") {
      setIfTokens((prev) => [...prev, newToken]);
    } else {
      setThenTokens((prev) => [...prev, newToken]);
    }
  };

  const handleDeleteIf = (id) => {
    setIfTokens((prev) => prev.filter((t) => t.id !== id));
  };

  const handleDeleteThen = (id) => {
    setThenTokens((prev) => prev.filter((t) => t.id !== id));
  };

  const handleDeleteCondition = () => {
    setIfTokens([]);
    setThenTokens([]);
  };

  // Validate IF: must follow PARAMETER OPERATOR VALUE [LOGIC PARAMETER OPERATOR VALUE]*
  const validateIf = (tokens) => {
    if (tokens.length === 0) return true;
    const expected = ["PARAMETER", "OPERATOR", "VALUE"];
    let idx = 0;
    for (const token of tokens) {
      if (idx < 3) {
        if (token.type !== expected[idx]) return false;
        idx++;
      } else {
        // After a complete triple, expect LOGIC or end
        if (token.type === "LOGIC") {
          idx = 0; // reset for next triple
        } else {
          return false;
        }
      }
    }
    return idx === 3; // must end on a complete triple
  };

  // Validate THEN: try to build and evaluate expression
  const validateThen = (tokens) => {
    if (tokens.length === 0) return true;
    try {
      const expr = tokens
        .map((t) => {
          if (t.type === "CONSTANT") return "1";
          if (t.type === "PARAMETER") return "1";
          if (t.type === "NUMBER") {
            const v = String(t.value).trim();
            if (v === "" || Number.isNaN(Number(v))) return "BAD";
            return v;
          }
          return t.value;
        })
        .join(" ");
      if (expr.includes("BAD")) return false;
      // eslint-disable-next-line no-new-func
      const result = Function(`"use strict"; return (${expr});`)();
      return typeof result === "number" && !Number.isNaN(result);
    } catch {
      return false;
    }
  };

  const ifValid = validateIf(ifTokens);
  const thenValid = validateThen(thenTokens);
  const hasError = hasCondition && (!ifValid || !thenValid);

  const dragClass = "formula-dragging-" + (isDragging ? "on" : "off");

  const sortableProps = {
    animation: 150,
    swapThreshold: 0.5,
    forceFallback: true,
    className: styles.sortable,
    filter: "[data-nodrag]",
    preventOnFilter: true,
    onStart: () => setIsDragging(true),
    onEnd: () => setIsDragging(false),
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--gap)",
        padding: "0 var(--horizontal-padding)",
        marginTop: "var(--gap)",
        marginBottom: "var(--gap)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={{ width: "var(--width-32)" }}>
          <Label>Sąlyga</Label>
        </div>
        <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            {hasCondition && (
              <>
                <div style={{ width: "var(--width-12)", flexShrink: 0 }}>
                  <Label>JEI</Label>
                </div>
                <div className={classNames(styles.formulaEditor, dragClass, { [styles.errorInFormula]: hasError && !ifValid })}>
                  <ReactSortable
                    list={ifTokens}
                    setList={setIfTokens}
                    {...sortableProps}
                    group="condition-if"
                  >
                    {ifTokens.map((token) => (
                      <div key={token.id} className={styles.itemWrapper}>
                        <Token
                          token={token}
                          onDelete={
                            disableChange
                              ? undefined
                              : () => handleDeleteIf(token.id)
                          }
                        />
                      </div>
                    ))}
                  </ReactSortable>
                </div>
                <div style={{ width: "var(--gap)" }} />
                <div style={{ width: "var(--width-12)", flexShrink: 0 }}>
                  <Label>TAI</Label>
                </div>
                <div className={classNames(styles.formulaEditor, dragClass, { [styles.errorInFormula]: hasError && !thenValid })}>
                  <ReactSortable
                    list={thenTokens}
                    setList={setThenTokens}
                    {...sortableProps}
                    group="condition-then"
                  >
                    {thenTokens.map((token) => (
                      <div key={token.id} className={styles.itemWrapper}>
                        <Token
                          token={token}
                          onDelete={
                            disableChange
                              ? undefined
                              : () => handleDeleteThen(token.id)
                          }
                        />
                      </div>
                    ))}
                  </ReactSortable>
                </div>
                <div style={{ width: "var(--gap)" }} />
              </>
            )}
            <Button
              disabled={disableChange}
              onClick={() => setIsAddOpen(true)}
              style={{
                width: "var(--width-8)",
                height: "var(--width-8)",
                minWidth: "var(--width-8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                flexShrink: 0,
              }}
            >
              +
            </Button>
          </div>
          {hasCondition && (
            <Button
              inline
              icon="trashIcon"
              disabled={disableChange}
              onClick={handleDeleteCondition}
            >
              Ištrinti sąlygą
            </Button>
          )}
        </div>
      </div>

      <AddConditionElementModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onAdd={handleAdd}
        parameters={parameters}
      />
    </div>
  );
};

export default ConditionBlock;
